import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { OnboardingHouseholdCreateRequest } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';
import { HouseholdQueryKeys } from 'views/household/household-queries';

export const useOnboardingHouseholds = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();
    return useQuery({
        queryKey: ['onboarding', 'list', tenantGUIDs],
        enabled: tenantGUIDs.length > 0,
        queryFn: async () => {
            const [statuses] = await CustomerApi().listOnboardingStatuses(tenantGUIDs);
            return statuses ?? [];
        },
    });
};

export const useOnboardingAccountsSummaries = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();
    return useQuery({
        queryKey: ['onboarding', 'accounts-summary', tenantGUIDs],
        enabled: tenantGUIDs.length > 0,
        queryFn: async () => {
            const [summaries] = await CustomerApi().listOnboardingAccountsSummaries(tenantGUIDs);
            return summaries ?? [];
        },
    });
};

export const useCreateOnboardingHousehold = () => {
    const queryClient = useQueryClient();
    const tenantGUIDs = useSelectedTenantGUIDs();
    return useMutation({
        mutationFn: async ({
            tenantGUID,
            request,
        }: {
            tenantGUID: string;
            request: OnboardingHouseholdCreateRequest;
        }) => {
            const [status] = await CustomerApi().createOnboardingHousehold(tenantGUID, request);
            if (!status) throw new Error('The onboarding household was not returned');
            return status;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['onboarding', 'list'] });
            void queryClient.invalidateQueries({ queryKey: HouseholdQueryKeys.allList() });
            void queryClient.invalidateQueries({ queryKey: HouseholdQueryKeys.ccList(tenantGUIDs) });
            void queryClient.invalidateQueries({ queryKey: HouseholdQueryKeys.map(tenantGUIDs) });
        },
    });
};
